/**
 * 📤 Employee Export Service
 * Handles employee data export to Excel and CSV formats.
 *
 * The Excel export sends the employees' infinityCodes to the backend, which
 * fetches the complete records from the database and returns a workbook with
 * EVERY schema column. The client-side row builder (used as the legacy `data`
 * payload and for the CSV fallback) mirrors the same full field list from the
 * locally loaded records.
 */

import { apiFetch } from "../../../context/AuthContext";

const API_URL = process.env.REACT_APP_BASE_URL;

/**
 * Full employee field catalog — mirrors the backend Prisma schema.
 * `key` is the field name on the raw employee record (fullEmployeeData),
 * `header` is the exported column name (the real DB column name).
 */
export const FULL_EXPORT_FIELDS = [
  { key: "sr", header: "English Name", type: "raw" },
  { key: "clientName", header: "Client Name", type: "raw" },
  { key: "clientCode", header: "Client Code", type: "raw" },
  { key: "arabicName", header: "Arabic Name", type: "raw" },
  { key: "idRegion", header: "ID Region", type: "raw" },
  { key: "position", header: "Position", type: "raw" },
  { key: "department", header: "Department", type: "department" },
  { key: "location", header: "Location", type: "raw" },
  { key: "nationalId", header: "National ID", type: "raw" },
  { key: "dob", header: "DOB", type: "date" },
  { key: "age", header: "Age", type: "raw" },
  { key: "gender", header: "Gender", type: "raw" },
  { key: "phoneNumber", header: "Phone number", type: "raw" },
  { key: "emergencyPhoneNumber", header: "Emergency Phone Number", type: "raw" },
  { key: "hiringDate", header: "Hiring Date", type: "date" },
  { key: "resignedDate", header: "Resigned Date", type: "date" },
  { key: "resignReasons", header: "Resign Reasons", type: "raw" },
  { key: "beRegularDate", header: "Be-Regular Date", type: "date" },
  { key: "toBeRegular", header: "To Be Regular Or Not", type: "boolean" },
  { key: "workingAgeYears", header: "working age(year)", type: "raw" },
  { key: "workingAgeMonths", header: "working age(month)", type: "raw" },
  { key: "periodForBeingRegular", header: "Period  for being regular", type: "raw" },
  { key: "laborMode", header: "labor mode", type: "raw" },
  { key: "contractCompany", header: "Contract Company", type: "raw" },
  { key: "contractDate", header: "Contract Date", type: "date" },
  { key: "termOfContract", header: "Term of contract", type: "raw" },
  { key: "contractExpiration", header: "Contract Expiration", type: "date" },
  { key: "insuredBeginningDate", header: "beginning date for the insured", type: "date" },
  { key: "insuranceStatus", header: "Insurance Status", type: "raw" },
  { key: "monthsForInsurance", header: "months for insurance", type: "raw" },
  { key: "insuranceNumber", header: "Insurance Number", type: "raw" },
  { key: "insuranceSalary", header: "Insurance Salary", type: "raw" },
  { key: "email", header: "Email", type: "raw" },
  { key: "address", header: "Address", type: "raw" },
  { key: "arabicAddress", header: "Arabic Address", type: "raw" },
  { key: "city", header: "City", type: "raw" },
  { key: "country", header: "Country", type: "raw" },
  { key: "religion", header: "Religion", type: "raw" },
  { key: "maritalStatus", header: "Marital Status", type: "raw" },
  { key: "nationality", header: "Nationality", type: "raw" },
  { key: "graduateInstitution", header: "Graduate institutions and universities", type: "raw" },
  { key: "major", header: "Major", type: "raw" },
  { key: "graduationYear", header: "Graduation year", type: "raw" },
  { key: "idCopy", header: "ID Copy", type: "boolean" },
  { key: "gradCertificateOriginal", header: "original copy of Graduation certificate", type: "boolean" },
  { key: "birthCertificateCopy", header: "Original and copy of birth certificate", type: "boolean" },
  { key: "militaryServiceCertificate", header: "Original military service certificate", type: "boolean" },
  { key: "laborLetterOriginal", header: "Original labor letter", type: "boolean" },
  { key: "noCrimeCertificate", header: "No crime certificate", type: "boolean" },
  { key: "photosCount", header: "6 Photos", type: "boolean" },
  { key: "uniformSize", header: "uniform size", type: "raw" },
  { key: "formNo6Original", header: "original copy of Form No.6", type: "boolean" },
  { key: "medicalCheckForm111", header: "Medical Check Form111", type: "boolean" },
  { key: "didMedicalCheck", header: "Did Medical Check or Not", type: "boolean" },
  { key: "medicalReportOriginal", header: "Original copy of medical examination report", type: "boolean" },
  { key: "lawyerCertificateCopy", header: "Copy of lawyer's certificate", type: "boolean" },
  { key: "driversLicenseCopy", header: "Copy of driver's license", type: "boolean" },
  { key: "testEngineerCertificate", header: "Copy of Test engineer's certificate", type: "boolean" },
  { key: "accountantCertificate", header: "Copy of accountant certificate", type: "boolean" },
  { key: "militaryStatus", header: "Militry Status", type: "raw" },
  { key: "healthCertificateOB", header: "Health Certificate For Office Boys", type: "boolean" },
  { key: "healthCertExpiryDate", header: "Health Certificate Expiry Date", type: "date" },
  { key: "healthCertStillValid", header: "Health Certificate Still Available or not", type: "boolean" },
  { key: "drivingLicenseDegree", header: "Drive license Degree", type: "raw" },
  { key: "drivingLicenseExpiry", header: "Driving License Expiry date", type: "date" },
  { key: "drivingLicenseStillValid", header: "Drive.License Still Available", type: "boolean" },
  { key: "lastContractDate", header: "Date of last contract", type: "date" },
  { key: "lastContractTerm", header: "Term of last contract", type: "raw" },
  { key: "lastContractExpiration", header: "Expiration of last contract", type: "date" },
  { key: "medicalInsuranceStage", header: "Medical Insurance Stage", type: "raw" },
  { key: "delivered", header: "Delivered", type: "boolean" },
  { key: "medicalNumber", header: "Medical Number", type: "raw" },
  { key: "netSalary", header: "Net Salary", type: "raw" },
  { key: "accountNumber", header: "Account Number", type: "raw" },
  { key: "employeeState", header: "Employee state", type: "raw" },
  { key: "firstLineManagerClientCode", header: "First Line Manager Client Code", type: "raw" },
  { key: "firstLineManagerName", header: "First Line Manager Name", type: "raw" },
  { key: "secondLineManagerClientCode", header: "Second Line Manager Client Code", type: "raw" },
  { key: "secondLineManagerName", header: "Second Line Manager Name", type: "raw" },
  { key: "transportationAllowances", header: "Transportation Allowances", type: "raw" },
  { key: "variableSalary", header: "Variable Salary", type: "raw" },
  { key: "workingCompany", header: "Working Company", type: "raw" },
  { key: "created_at", header: "Created At", type: "date" },
  { key: "updated_at", header: "Updated At", type: "date" },
];

const formatExportValue = (value, type) => {
  if (value === null || value === undefined) return "";
  switch (type) {
    case "date": {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime())
        ? ""
        : parsed.toISOString().split("T")[0];
    }
    case "boolean":
      return value ? "Yes" : "No";
    case "department":
      // Raw rows carry department as {id,name}; mapped rows carry the name string.
      return typeof value === "object" ? value?.name ?? "" : value;
    default:
      return value;
  }
};

/**
 * Resolve an employee's infinityCode from a mapped table row.
 */
export const getEmployeeInfinityCode = (employee) =>
  employee?.fullEmployeeData?.infinityCode ||
  employee?.infinityCode ||
  employee?.id ||
  null;

/**
 * Prepare employee data for export with EVERY database field, built from the
 * raw record kept on each row (fullEmployeeData).
 * @param {Array} employees - Array of mapped employee rows
 * @returns {Array} Formatted export rows keyed by DB column name
 */
export const prepareExportData = (employees) => {
  return employees.map((employee) => {
    const raw = employee.fullEmployeeData || employee;
    const row = {};
    FULL_EXPORT_FIELDS.forEach(({ key, header, type }) => {
      const value = raw[key] !== undefined ? raw[key] : employee[key];
      row[header] = formatExportValue(value, type);
    });
    return row;
  });
};

/**
 * Generate filename for export based on filters and timestamp
 * @param {Object} filters - Filter criteria
 * @returns {string} Generated filename
 */
export const generateExportFilename = (filters = {}) => {
  let filename = "employees_filtered";
  if (filters.searchTerm) filename += `_search-${filters.searchTerm}`;
  if (filters.filterDepartment) filename += `_dept-${filters.filterDepartment}`;
  if (filters.filterStatus) filename += `_status-${filters.filterStatus}`;
  if (filters.filterCompany) filename += `_company-${filters.filterCompany}`;
  filename += `_${new Date().getTime()}`;
  return filename;
};

const csvEscape = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

/**
 * Generate CSV content from employee data (full database field set)
 * @param {Array} employees - Array of mapped employee rows
 * @returns {string} CSV content
 */
export const generateCSV = (employees) => {
  if (employees.length === 0) return "";

  const rows = prepareExportData(employees);
  const headers = FULL_EXPORT_FIELDS.map(({ header }) => header);

  const csvRows = [headers.map(csvEscape).join(",")];
  rows.forEach((row) => {
    csvRows.push(headers.map((header) => csvEscape(row[header])).join(","));
  });

  return csvRows.join("\n");
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content
 * @param {string} filename - Filename without extension
 */
export const downloadCSV = (csvContent, filename) => {
  // Create blob with CSV content
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  // Create download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  // Add to DOM, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download blob as Excel file
 * @param {Blob} blob - File blob
 * @param {string} filename - Filename without extension
 */
export const downloadExcel = (blob, filename) => {
  // Create blob URL for download
  const url = window.URL.createObjectURL(blob);
  // Create download link element
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${filename}.xlsx`);
  // Add link to DOM and trigger click
  document.body.appendChild(link);
  link.click();
  // Clean up
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Export employees to Excel via API.
 * Sends infinityCodes so the backend can export the complete database
 * records; the client-built rows are included as a fallback for backends
 * that only support the legacy `data` payload.
 * @param {Array} employees - Array of mapped employee rows
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Blob>} Excel file blob
 */
export const exportToExcelAPI = async (employees, filters = {}) => {
  const infinityCodes = employees
    .map(getEmployeeInfinityCode)
    .filter(Boolean);

  // Preferred: send only the infinityCodes — the backend fetches the complete
  // records itself. Row data is sent ONLY as a legacy fallback when no codes
  // resolve, since free-text employee data can trip the request scanner.
  const body = { filters: { ...filters, totalRecords: employees.length } };
  if (infinityCodes.length > 0) {
    body.infinityCodes = infinityCodes;
  } else {
    body.data = prepareExportData(employees);
  }

  const response = await apiFetch(`${API_URL}/employees/export-filtered`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error("Failed to export employees");
  }

  // Get blob from response
  return await response.blob();
};

/**
 * Export employees to Excel (with CSV fallback)
 * @param {Array} employees - Array of employee objects
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Export result with success status and metadata
 */
export const exportEmployees = async (employees, filters = {}) => {
  const filename = generateExportFilename(filters);

  try {
    // Try Excel export first
    const blob = await exportToExcelAPI(employees, filters);
    downloadExcel(blob, filename);

    return {
      success: true,
      format: "excel",
      count: employees.length,
      filename: `${filename}.xlsx`,
    };
  } catch (err) {
    console.error("Excel export failed, falling back to CSV:", err);

    // Fallback to CSV export
    const csvContent = generateCSV(employees);
    downloadCSV(csvContent, filename);

    return {
      success: true,
      format: "csv",
      count: employees.length,
      filename: `${filename}.csv`,
    };
  }
};
